import menuIcon from 'assets/images/icons/button/menu.svg';
import OverlaySpinner from 'components/spinners/OverlaySpinner';
import { ActionImageGreenButton, ActionPrimaryButton, CompilerActionSecondaryButton } from 'components/v2/buttons';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import ConfirmEditorialSendToPublishDialog from 'components/v2/dialogs/ConfirmEditorialSendToPublishDialog';
import ConfirmEditorialSendToPublishProcessCertificationDialog from 'components/v2/dialogs/ConfirmEditorialSendToPublishProcessCertificationDialog';
import { ConfirmWithCommentPayload } from 'components/v2/dialogs/ConfirmWithCommentDialog';
import DeclinePublicationDialog from 'components/v2/dialogs/DeclinePublicationDialog';
import DeclineVerificationDialog from 'components/v2/dialogs/DeclineVerificationDialog';
import DepublishAdminDialog, { DepublishWithReasonSelectionPayload } from 'components/v2/dialogs/DepublishAdminDialog';
import { ProcessStatus } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { EpdLinks } from 'routes/EpdRoutes';
import { ActionState, EPDModel, VerificationType } from 'services/EpdClient';
import EpdStateService from 'services/EpdStateService';
import styled from 'styled-components';
import { ErrorMessage, H2 } from 'styles/Styles.styled';
import { PoorMansError } from 'util/utils';
import * as Yup from 'yup';
import { WizardStepValidationSchema } from './types';
import Tooltip from 'components/controls/Tooltip';
import UpdateEpdTypeSelectionDialog from 'components/v2/dialogs/UpdateEpdTypeSelectionDialog';
import LicenseeAdminReviewDialog from 'components/v2/dialogs/LicenseeAdminReviewDialog';
import { getEPDPublicationProcessingTimeFrame } from 'services/api/epdApi';
import { QUERY_KEYS } from 'services/api/constants';
import { EPDPublicationProcessingTimeframeModel } from 'types/types';
import { ACCESS_DENIED_DETAIL, getEpdActionErrorMessage } from 'util/epdActionErrorUtils';

type TProps = {
  epd: EPDModel;
  companyId: string;
  updateEpd: (state: {} | null) => void;
  agreementConsent: boolean;
  validationStep1: WizardStepValidationSchema;
};

type VisibleDialogType =
  | 'unlock'
  | 'cancelUpdate'
  | 'deleteDraft'
  | 'declineVerification'
  | 'declinePublication'
  | 'depublishByAdmin'
  | 'confirmEditorialSendToPublish'
  | 'licenseeAdminReviewDialog';

const ToolButtons = ({ epd, companyId, updateEpd, agreementConsent, validationStep1 }: TProps) => {
  const queryClient = useQueryClient();
  const { changeCompany, licensee } = useContext(CompanyContext);
  const { t } = useTranslation();
  const history = useHistory();
  const [visibleDialog, setVisibleDialog] = useState<VisibleDialogType | null>(null);
  const [isDeregisterModalOpen, setIsDeregisterModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [epdUpdateType, setEpdUpdateType] = useState<'update' | 'editorialUpdate'>('update');
  const actionMenuRef = useRef<Menu | null>(null);

  useEffect(() => {
    const showToastRegisterEditorial = localStorage.getItem('showToastRegisterEditorial');
    if (showToastRegisterEditorial !== 'true') {
      return;
    }
    const fetchAndShowToast = async () => {
      const timeframe = await getEPDPublicationProcessingTimeFrame();
      toast.success(sentForPublicationToastTemplate(timeframe, licensee?.name !== 'defaultLicensee'), {
        autoClose: false,
        style: { minWidth: '30rem' },
      });
    };
    fetchAndShowToast();
    localStorage.setItem('showToastRegisterEditorial', 'false');
  }, []);

  const handleEpdActionError = (error: unknown, fallbackMessage?: string) => {
    const epdErrorMsg = getEpdActionErrorMessage(error);

    if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
      toast.error(epdErrorMsg, { position: 'top-center' });
      history.push(EpdLinks.dashboard());
      return;
    }

    toast.error(epdErrorMsg ?? fallbackMessage ?? PoorMansError(error), {
      position: 'top-center',
    });
  };

  const changeDeregisterModalState = () => {
    setIsDeregisterModalOpen(!isDeregisterModalOpen);
  };

  const hideDialog = () => {
    setVisibleDialog(null);
  };

  const sentForPublicationToastTemplate = (
    publicationProcessingTimeframe: EPDPublicationProcessingTimeframeModel,
    hasLicensee: boolean
  ): ReactNode => {
    return (
      <div className="">
        <SendForPublishSuccessNotificationTitle>
          {'Thank you for registering your EPD with the International EPD System!'}
        </SendForPublishSuccessNotificationTitle>
        <SendForPublishSuccessNotificationParagraph>
          {hasLicensee ? (
            <>
              We will attend to it as soon as possible. We aim to publish the EPD within{' '}
              {publicationProcessingTimeframe?.minProcessingDays}-{publicationProcessingTimeframe?.maxProcessingDays} working
              days after the initial review by the Licensee. If you have any questions, feel free to contact the Secretariat
              at support@environdec.com.
            </>
          ) : (
            <>
              We will attend to it as soon as possible. We aim to publish the EPD within{' '}
              {publicationProcessingTimeframe?.minProcessingDays}-{publicationProcessingTimeframe?.maxProcessingDays} working
              days. If you have any questions, feel free to contact the Secretariat at support@environdec.com.
            </>
          )}
        </SendForPublishSuccessNotificationParagraph>
        <SendForPublishSuccessNotificationParagraph>
          {
            'If any information is missing or if any irregularities are recognized in the submitted material, the Secretariat will contact you.'
          }
        </SendForPublishSuccessNotificationParagraph>
      </div>
    );
  };

  const onSendForPublicationEditorial = async (paylod: ConfirmWithCommentPayload | null) => {
    try {
      setErrorMessage(undefined);

      if (!epd?.id) {
        setStatus(ProcessStatus.Error);
        throw new Error('EPD identifier is undefined.');
      }

      const result = await EpdStateService.sendForPublicationEditorial(epd.id, paylod?.commentText);

      updateEpd(result ?? null);
      setStatus(ProcessStatus.Success);

      localStorage.setItem('showToastRegisterEditorial', 'true');
      history.go(0);
    } catch (error) {
      setStatus(ProcessStatus.Error);

      const epdErrorMsg = getEpdActionErrorMessage(error);

      if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
        toast.error(epdErrorMsg, { position: 'top-center' });
        history.push(EpdLinks.dashboard());
        return;
      }

      if (epdErrorMsg) {
        toast.error(epdErrorMsg, { position: 'top-center' });
        return;
      }

      setErrorMessage(PoorMansError(error));
    }
  };

  const onSendForPublication = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      if (!epd?.id) {
        setStatus(ProcessStatus.Error);
        toast.error('EPD identifier is undefined', {
          position: 'top-center',
        });
        return null;
      }

      const result = await EpdStateService.sendForPublication(epd?.id);

      updateEpd(result ?? null);
      const timeframe = await getEPDPublicationProcessingTimeFrame();
      toast.success(sentForPublicationToastTemplate(timeframe, licensee?.name !== 'defaultLicensee'), {
        autoClose: false,
        style: { minWidth: '30rem' },
      });
      setStatus(ProcessStatus.Success);
    } catch (error) {
      setStatus(ProcessStatus.Error);
      const epdErrorMsg = getEpdActionErrorMessage(error);

      if (epdErrorMsg === ACCESS_DENIED_DETAIL) {
        toast.error(epdErrorMsg, { position: 'top-center' });
        history.push(EpdLinks.dashboard());
        return;
      }

      toast.error(epdErrorMsg ?? PoorMansError(error), {
        position: 'top-center',
      });
    }
  };

  const handleDeleteEpd = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      await EpdStateService.delete(epd.id);
      setStatus(ProcessStatus.Success);
      toast.success(
        t('messages.epdDeleted', {
          epdNumber: epd?.shortIdentificationNumber,
        }) as string,
        {
          position: 'top-center',
        }
      );
      hideDialog();
      changeCompany?.(companyId);
      history.push(EpdLinks.dashboard());
    } catch (error) {
      setStatus(ProcessStatus.Error);
      handleEpdActionError(error, 'EPD has not been deleted due to unexpected error.');
    }
  };

  const handleDeregister = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      await EpdStateService.deregister(epd.id);
      setStatus(ProcessStatus.Success);
      changeDeregisterModalState();
      history.go(0);
    } catch (error) {
      setStatus(ProcessStatus.Error);
      handleEpdActionError(error, 'EPD has not been deregistered due to unexpected error.');
    }
  };

  const handleCancelUpdateEpd = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      await EpdStateService.cancelUpdate(epd.id);
      setStatus(ProcessStatus.Success);
      toast.success(t('messages.epdCancelled') as string, {
        position: 'top-center',
      });
      hideDialog();
      changeCompany?.(companyId);
    } catch (error) {
      setStatus(ProcessStatus.Error);
      handleEpdActionError(error, 'EPD update cancellation has not been accomplished due to unexpected error.');
    }
  };

  const handleCancelPublicationClicked = () => {
    confirmAlert({
      caption: t('confirmModal.cancelPublicationEPD', {
        name: `${epd.fullIdentificationNumber} ${epd.title}`,
      }),
      description: 'You may interrupt the EPD reviewing process.',
      acceptLabel: t('Submit'),
      onConfirm: handleCancelPublicationConfirmed,
    });
  };

  const handleCancelPublicationConfirmed = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      const result = await EpdStateService.cancelPublication(epd.id);
      updateEpd(result);
      setStatus(ProcessStatus.Success);
      toast.success(
        t('messages.canceledPublicationEpd', {
          name: `${epd.fullIdentificationNumber} ${epd.title}`,
        }) as string
      );
      hideDialog();
    } catch (error) {
      setStatus(ProcessStatus.Error);
      handleEpdActionError(error, 'EPD publication has not been canceled due to unexpected error.');
    }
  };

  const handleDeclineVerificationEpd = async ({ commentText }: ConfirmWithCommentPayload) => {
    try {
      setStatus(ProcessStatus.Fetching);
      await EpdStateService.declineVerification(epd.id, commentText);
      setStatus(ProcessStatus.Success);
      toast.success(
        t('messages.declinedVerification', {
          epdNumber: epd?.shortIdentificationNumber,
        }) as string
      );
      hideDialog();
      changeCompany?.(companyId);
      history.push(EpdLinks.dashboard());
    } catch (error) {
      setStatus(ProcessStatus.Error);
      handleEpdActionError(error, 'EPD verification has not been declined due to unexpected error.');
    }
  };

  const handleDeclinePublicationEpd = async ({ commentText }: ConfirmWithCommentPayload) => {
    try {
      setStatus(ProcessStatus.Fetching);
      const result = await EpdStateService.declinePublication(epd.id, commentText);
      updateEpd(result);
      setStatus(ProcessStatus.Success);
      toast.success(
        t('messages.declinedPublication', {
          epdNumber: epd?.shortIdentificationNumber,
        }) as string
      );
      hideDialog();
    } catch (error) {
      setStatus(ProcessStatus.Error);
      handleEpdActionError(error, 'EPD publication has not been declined due to unexpected error.');
    }
  };

  const handleLicenseeAdminSentToPublish = async ({ commentText }: ConfirmWithCommentPayload) => {
    try {
      setStatus(ProcessStatus.Fetching);
      const result = await EpdStateService.licenseeAdminSendToPublish(epd.id, commentText);
      updateEpd(result);
      setStatus(ProcessStatus.Success);
      toast.success('Operation completed successfully!');
      hideDialog();
    } catch (error) {
      setStatus(ProcessStatus.Error);
      handleEpdActionError(error, 'EPD has not been sent to publish due to unexpected error.');
    }
  };

  const handleEditorialSendToPublishConfirmed = (payload: ConfirmWithCommentPayload | null) => {
    setStatus(ProcessStatus.Fetching);

    setVisibleDialog(null);
    if (!payload) {
      setStatus(ProcessStatus.Error);
      return;
    }
    onSendForPublicationEditorial(payload);
  };

  const handleDepublishEpdByAdmin = async ({ commentText, depublishType }: DepublishWithReasonSelectionPayload) => {
    try {
      setStatus(ProcessStatus.Fetching);
      const result = await EpdStateService.depublish(epd.id, commentText, depublishType ?? null);
      updateEpd(result);
      setStatus(ProcessStatus.Success);
      toast.success(t('messages.epdDepublished', { epdNumber: epd?.fullIdentificationNumber }) as string);
      hideDialog();
    } catch (error) {
      setStatus(ProcessStatus.Error);
      handleEpdActionError(error, 'EPD has not been depublished due to unexpected error.');
    }
  };

  const onCancelUpdateEpd = () =>
    confirmAlert({
      caption: t('confirmModal.cancelUpdateEPD', {
        name: `${epd.fullIdentificationNumber} ${epd.title}`,
      }),
      description: <div>All added changes will be lost.</div>,
      acceptLabel: t('Submit'),
      rejectLabel: t('Cancel'),
      onConfirm: handleCancelUpdateEpd,
    });

  const onDeleteEpd = () =>
    confirmDelete({
      caption: t('confirmModal.deleteEPD', {
        name: `${epd.fullIdentificationNumber} ${epd.title}`,
      }),
      description: <div>The EPD data will be lost and you will not be able to restore it.</div>,
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: handleDeleteEpd,
    });

  const onDeregisterEpd = () =>
    confirmAlert({
      caption: t('confirmModal.deregisterEPD', {
        name: `${epd.fullIdentificationNumber} ${epd.title}`,
      }),
      description: <div>The EPD will be deregistered and shall no longer be used or referred to.</div>,
      acceptLabel: 'Deregister',
      rejectLabel: 'Cancel',
      onConfirm: handleDeregister,
    });

  const onCancelVerificationEpd = () =>
    confirmAlert({
      caption: t('confirmModal.cancelEPDVerification', {
        name: `${epd.fullIdentificationNumber} ${epd.title}`,
      }),
      description: <div>This action may interrupt Step 3 - EPD Verification process.</div>,
      acceptLabel: 'Submit',
      rejectLabel: 'Cancel',
      onConfirm: () => handleButtonCallFunction(EpdStateService.cancelVerification),
    });

  const onDepublishByOwnerEpd = () =>
    confirmAlert({
      caption: t('confirmModal.depublishEPDByOwner', {
        name: `${epd.fullIdentificationNumber} ${epd.title}`,
      }),
      description: (
        <>
          The EPD will be depublished and no longer available on environdec.com. To publish your EPD again, you will need to
          contact the Secretariat.
        </>
      ),
      acceptLabel: 'Depublish',
      rejectLabel: 'Cancel',
      onConfirm: () => handleButtonCallFunction(EpdStateService.depublish),
    });

  const actionMenuItems: MenuItem[] = [
    {
      label: 'Depublish EPD',
      command: () => setVisibleDialog('depublishByAdmin'),
      visible: epd?.epdState?.depublishByAdmin === ActionState.Enabled,
    },
    {
      label: 'Depublish EPD',
      command: onDepublishByOwnerEpd,
      visible: epd?.epdState?.depublishByCustomer === ActionState.Enabled,
    },
    {
      label: 'Deregister EPD',
      command: onDeregisterEpd,
      visible: epd?.epdState?.deregister === ActionState.Enabled,
    },
    {
      label: 'Cancel update',
      command: onCancelUpdateEpd,
      visible: epd?.epdState?.cancelUpdate === ActionState.Enabled,
    },
  ];

  const handlePublish = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      if (!epd?.id) {
        setStatus(ProcessStatus.Error);
        toast.error('EPD identifier is undefined', {
          position: 'top-center',
        });
        return null;
      }
      await EpdStateService.publish(epd.versionId, epd?.id).then(() => updateEpd({}));

      setStatus(ProcessStatus.Success);
      toast.success(
        t('messages.epdPublished', {
          epdNumber: epd?.fullIdentificationNumber ?? epd?.shortIdentificationNumber,
        }) as string
      );
    } catch (error) {
      setStatus(ProcessStatus.Error);
      toast.error(PoorMansError(error), {
        position: 'top-center',
      });
    }
  };

  const handleStartVerification = async () => {
    // This is pure copy-paste from WizardStep1.tsx
    let isValidVerifier = true;
    try {
      validationStep1.validateSyncAt('epdVerifier', epd);
    } catch (e) {
      const validationError = e as Yup.ValidationError;
      isValidVerifier = validationError.type !== 'excluded-from-company';
    }

    if (isValidVerifier) {
      await handleButtonCallFunction(EpdStateService.startVerification);
    } else {
      confirmAlert({
        caption: 'You are about to send the EPD for verification, however, the verifier you have selected is not available.',
        description: (
          <div>
            Please select an active verifier from the list. If you are making an editorial update that does not require
            verification, please describe the change and send the EPD for publication.
          </div>
        ),
        acceptLabel: '',
        rejectLabel: 'Close',
        onConfirm: null,
        infoOnly: true,
      });
    }
  };

  const handleButtonCallFunction = async (callFunction: any) => {
    try {
      setStatus(ProcessStatus.Fetching);
      if (!epd?.id) {
        setStatus(ProcessStatus.Error);
        toast.error('EPD identifier is undefined', {
          position: 'top-center',
        });
        return null;
      }
      const result = await callFunction(epd?.id);

      updateEpd(result ?? null);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_VERSIONS, epd.id] });
      setStatus(ProcessStatus.Success);
    } catch (error) {
      setStatus(ProcessStatus.Error);
      toast.error(PoorMansError(error), {
        position: 'top-center',
      });
    }
  };

  const startUpdatingHandler = async (updateType: 'update' | 'editorialUpdate') => {
    if (updateType === 'update') {
      await handleButtonCallFunction(EpdStateService.unlock);
    } else {
      await handleButtonCallFunction(EpdStateService.editorialUpdating);
    }
  };

  const onStartUpdatingEpd = async () => {
    const shouldShowUpdateTypeSelection =
      epd.verificationType === VerificationType.EPDVerification && epd.useOldUpdateFlow === false;

    if (!shouldShowUpdateTypeSelection) {
      await handleButtonCallFunction(EpdStateService.unlock);
      return;
    }

    setVisibleDialog('unlock');
  };

  const now = new Date();

  const disableStart = new Date(now.getFullYear(), 11, 30, 0, 0, 0); // December 30, 00:00 local time
  const disableEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59); // December 31, 23:59 local time

  const publishingEpdIsDisabledDueToYearEnd = now >= disableStart && now <= disableEnd;

  const sendEpdForPublicationState =
    epd?.epdState?.sendForPublication === ActionState.Enabled && (!agreementConsent || publishingEpdIsDisabledDueToYearEnd)
      ? ActionState.Disabled
      : epd?.epdState?.sendForPublication;

  const currentEPDVersionName = `${epd?.fullIdentificationNumber} ${epd?.title}`;

  return (
    <>
      <ConfirmDialog />
      {status === ProcessStatus.Fetching && <OverlaySpinner />}
      <WizardHeaderGrid>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', maxWidth: '60%' }}>
          <TitleText style={{ minWidth: '5rem' }}>
            {currentEPDVersionName}
            <Tooltip position="title">{currentEPDVersionName}</Tooltip>
          </TitleText>
          <H2>
            {epd.epdState && (
              <span style={{ fontWeight: 700 }}>
                &nbsp;-&nbsp;{t(`epdWizard.epdState.status.${epd.epdState.statusDisplayName}`)}
              </span>
            )}
          </H2>
        </div>
        <ButtonGroup>
          <CompilerActionSecondaryButton
            actionState={epd?.epdState?.deleteDraft}
            label="Delete EPD"
            onClick={onDeleteEpd}
            outlined
          />
          <CompilerActionSecondaryButton
            actionState={epd?.epdState?.declineVerification}
            label={t('epdWizard.epdState.action.declineEpd')}
            onClick={() => setVisibleDialog('declineVerification')}
            outlined
          />
          <DeclineVerificationDialog
            isOpened={visibleDialog === 'declineVerification'}
            epd={epd}
            onClose={(payload) => {
              hideDialog();
              if (payload) {
                handleDeclineVerificationEpd(payload);
              }
            }}
          />
          <CompilerActionSecondaryButton
            actionState={epd?.epdState?.declinePublication}
            label={t('epdWizard.epdState.action.declineEpd')}
            onClick={() => setVisibleDialog('declinePublication')}
            outlined
          />
          <DeclinePublicationDialog
            isOpened={visibleDialog === 'declinePublication'}
            epd={epd}
            onClose={(payload) => {
              hideDialog();
              if (payload) {
                handleDeclinePublicationEpd(payload);
              }
            }}
          />
          {sendEpdForPublicationState != ActionState.Hidden ||
          epd?.epdState?.sendForPublicationEditorial != ActionState.Hidden ? (
            <CompilerActionSecondaryButton
              label={t('epdWizard.epdState.action.startVerification')}
              onClick={handleStartVerification}
              actionState={epd?.epdState?.startVerification}
            />
          ) : (
            <ActionPrimaryButton
              label={t('epdWizard.epdState.action.startVerification')}
              onClick={handleStartVerification}
              actionState={epd?.epdState?.startVerification}
            />
          )}
          <ActionPrimaryButton
            label={t('epdWizard.epdState.action.cancelVerification')}
            onClick={onCancelVerificationEpd}
            actionState={epd?.epdState?.cancelVerification}
          />
          <ActionPrimaryButton
            label={t('epdWizard.epdState.action.cancelPublication')}
            onClick={handleCancelPublicationClicked}
            actionState={epd?.epdState?.cancelPublication}
          />
          <ActionPrimaryButton
            label={t('epdWizard.epdState.action.sendToPublish')}
            onClick={onSendForPublication}
            actionState={sendEpdForPublicationState}
          />
          {epd.verificationType != VerificationType.EPDProcessCertification ? (
            <ConfirmEditorialSendToPublishDialog
              isOpened={visibleDialog === 'confirmEditorialSendToPublish'}
              onClose={handleEditorialSendToPublishConfirmed}
            />
          ) : (
            <ConfirmEditorialSendToPublishProcessCertificationDialog
              isOpened={visibleDialog === 'confirmEditorialSendToPublish'}
              onClose={handleEditorialSendToPublishConfirmed}
              verificationStepName="Step 3 - EPD Verification"
            />
          )}
          <ActionPrimaryButton
            label={t('epdWizard.epdState.action.cancelDeregistration')}
            onClick={() => handleButtonCallFunction(EpdStateService.cancelDeregisteration)}
            actionState={epd?.epdState?.cancelDeregistration}
          />
          {epd.verificationType === VerificationType.EPDVerification && epd.useOldUpdateFlow === false ? (
            <ActionPrimaryButton
              label={t('epdWizard.epdState.action.updateEpd')}
              onClick={onStartUpdatingEpd}
              actionState={epd?.epdState?.unlock}
            />
          ) : (
            <ActionPrimaryButton
              label={t('epdWizard.epdState.action.updateEpd')}
              onClick={() => handleButtonCallFunction(EpdStateService.unlock)}
              actionState={epd?.epdState?.unlock}
            />
          )}
          <ActionPrimaryButton
            label={t('epdWizard.epdState.action.retractVerification')}
            onClick={() => handleButtonCallFunction(EpdStateService.cancelApproveVerification)}
            actionState={epd?.epdState?.retractVerification}
          />
          <ActionPrimaryButton
            label="Approve EPD"
            onClick={() => handleButtonCallFunction(EpdStateService.approve)}
            actionState={epd?.epdState?.approveVerification}
          />
          <ActionPrimaryButton label="Publish EPD" onClick={handlePublish} actionState={epd?.epdState?.publish} />
          <DepublishAdminDialog
            isOpened={visibleDialog === 'depublishByAdmin'}
            epd={epd}
            onClose={(payload) => {
              hideDialog();
              if (payload) {
                handleDepublishEpdByAdmin(payload);
              }
            }}
          />
          <ActionPrimaryButton
            label="Send to publish"
            onClick={() => setVisibleDialog('confirmEditorialSendToPublish')}
            actionState={epd?.epdState?.sendForPublicationEditorial}
          />
          <ActionPrimaryButton
            label={t('epdWizard.epdState.action.licenseeAdminSendToPublish')}
            onClick={() => setVisibleDialog('licenseeAdminReviewDialog')}
            actionState={epd?.epdState?.licenseeAdminSendToPublish}
          />
          <LicenseeAdminReviewDialog
            isOpened={visibleDialog === 'licenseeAdminReviewDialog'}
            onClose={(payload) => {
              hideDialog();
              if (payload) {
                handleLicenseeAdminSentToPublish(payload);
              }
            }}
          />
          {actionMenuItems.filter((m) => m.visible).length > 0 && (
            <ActionImageGreenButton
              onClick={(e: React.SyntheticEvent) => actionMenuRef.current?.toggle(e)}
              actionState={ActionState.Enabled}
            >
              <img alt="menu" src={menuIcon} />
            </ActionImageGreenButton>
          )}
          <Menu ref={actionMenuRef} model={actionMenuItems} popupAlignment={'right'} popup />
          {errorMessage && <ErrorMessage style={{ marginTop: '1rem' }}> {errorMessage}</ErrorMessage>}
        </ButtonGroup>
      </WizardHeaderGrid>
      <UpdateEpdTypeSelectionDialog
        visible={visibleDialog === 'unlock'}
        updateType={epdUpdateType}
        onUpdateTypeChange={(value) => setEpdUpdateType(value)}
        onAccept={() => startUpdatingHandler(epdUpdateType)}
        onHide={() => setVisibleDialog(null)}
      />
    </>
  );
};

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  & > .p-button:not(:last-of-type) {
    margin-right: 16px;
  }
`;

const TitleText = styled.h2`
  ${(props) => props.theme.fonts.heading2}
  flex-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 20rem;
  margin: '1rem auto 1rem 1rem';
  cursor: pointer;
`;

export const WizardHeaderGrid = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const SendForPublishSuccessNotificationTitle = styled.b`
  font-family: Inter;
  font-weight: 600;
  line-height: 20px;
`;

const SendForPublishSuccessNotificationParagraph = styled.p`
  font-family: Inter;
  font-weight: 400;
  line-height: 20px;
  opacity: 70%;
  margin-top: 10px;
  margin-bottom: 0px;
`;

export default ToolButtons;
